import { http } from "@/shared/lib/api/http"

/** A reference to a person by any unique handle. Email is unique system-wide, so the backend can map
 *  a member <-> workspace user and fill in whatever the calling side is missing. */
export type IdentityRef = {
  memberId?: string | null
  userId?: string | null
  email?: string | null
}

export type IdentityPreviewDto = {
  memberId: string | null
  userId: string | null
  name: string
  avatarUrl: string | null
  /** Directory email + phone + status — returned only when the viewer has Members VIEW. */
  email: string | null
  phone: string | null
  memberStatus: string | null
  /** Account block — returned only when the viewer has Team/Settings VIEW. */
  account: {
    role: string
    status: string
    loginEmail: string
  } | null
}

type IdentityEnvelope = {
  data?: Partial<IdentityPreviewDto> | null
}

export function identityRefIsResolvable(ref: IdentityRef): boolean {
  return Boolean(ref.memberId || ref.userId || (ref.email && ref.email.includes("@")))
}

export async function fetchIdentityPreview(ref: IdentityRef): Promise<IdentityPreviewDto | null> {
  const params: Record<string, string> = {}
  if (ref.memberId) params.memberId = ref.memberId
  if (ref.userId) params.userId = ref.userId
  if (ref.email) params.email = ref.email
  if (Object.keys(params).length === 0) return null

  const response = await http.get<IdentityEnvelope>("/identity/preview", { params })
  const data = response.data?.data
  if (!data || typeof data !== "object") return null

  return {
    memberId: data.memberId ?? null,
    userId: data.userId ?? null,
    name: typeof data.name === "string" ? data.name : "",
    avatarUrl: data.avatarUrl ?? null,
    email: data.email ?? null,
    phone: data.phone ?? null,
    memberStatus: data.memberStatus ?? null,
    account: data.account ?? null,
  }
}
